import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import CredentialFieldEditor, {
  CredentialRow,
  collapseRowsForSubmit,
  hasAtLeastOneSecretValue,
  makeCredentialRow,
  rowIncompletenessByRowId,
  rowsHaveDuplicateNames,
} from '../../sidebar/CredentialFieldEditor';
import '../../sidebar/credential-field-editor.scss';

interface HumanTaskCredentialCreationFormProps {
  taskId: string;
  /**
   * Pre-fills the site field. We pass the host from the web_action task URL
   * when available so the operator doesn't have to retype it. Operator can
   * still edit the value before saving.
   */
  defaultSite?: string;
  onCancel: () => void;
  onSuccess: (credentialId: string) => void;
}

/**
 * Inline form for the ops worker to record an account they just created
 * during a human task. Same shape as every other credential entry surface
 * (Memories Add, in-task question, response artifact, edit-existing): the
 * operator types a Site + Label, then adds as many `(name, value, secret?)`
 * rows as the account needs. The default seed is `[username, password]`
 * because the overwhelming majority of accounts ops create are sign-in
 * pairs; the operator can add extras (PIN, security answers, account
 * reference) before saving.
 *
 * The ops surface is inline rather than a modal (the operator is in the
 * middle of executing a task, not navigating to a settings page) but the
 * field-management UX is the shared `CredentialFieldEditor` so the visual
 * weight matches the other surfaces.
 *
 * Helper buttons opt-in via `helpers` on the editor:
 *   - "Use user's email"  → fetches and fills the task creator's primary
 *                          email into identifier-shaped rows
 *   - "Generate"          → calls /memories/generate-password and fills
 *                          the secret row with a strong random string
 *   - "Copy"              → copies the visible value to clipboard so the
 *                          operator can paste it into the third-party site
 *
 * Submits to POST /human-tasks/:taskId/credentials/created.
 */
const HumanTaskCredentialCreationForm: React.FC<
  HumanTaskCredentialCreationFormProps
> = ({ taskId, defaultSite, onCancel, onSuccess }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [site, setSite] = useState(defaultSite || '');
  const [label, setLabel] = useState('');
  const [rows, setRows] = useState<CredentialRow[]>(() => [
    makeCredentialRow({
      name: 'username',
      value: '',
      isSecret: false,
      displayLabel: 'Username or email',
    }),
    makeCredentialRow({
      name: 'password',
      value: '',
      isSecret: true,
      displayLabel: 'Password',
    }),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache the user's email after the first fetch so subsequent clicks of
  // "Use user's email" are instant. The helper exposes its own loading
  // state via the button label — we don't need to surface anything here.
  const [cachedUserEmail, setCachedUserEmail] = useState<string | null>(null);

  // On mount, fetch user-info once to (a) warm the user-email cache and
  // (b) pre-fill any signupCredentialDefaults the PA surfaced (username /
  // notes the USER explicitly volunteered). Password is never pre-filled —
  // operators generate it via the editor's Generate helper.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(`/human-tasks/${taskId}/user-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const email: string | null = response.data?.userEmail || null;
        if (email) setCachedUserEmail(email);
        const defaults: { username?: string; notes?: string } | null =
          response.data?.signupCredentialDefaults || null;
        if (!defaults) return;
        // Apply defaults to the matching rows iff the operator hasn't
        // already typed in them. This effect runs once on mount so we
        // don't need to reconcile later edits.
        setRows((prev) =>
          prev.map((r) => {
            if (r.name === 'username' && !r.value && defaults.username) {
              return { ...r, value: defaults.username };
            }
            return r;
          }),
        );
        // The `notes` default is appended as an extra non-secret row when
        // the PA surfaced one, so the operator sees the context the user
        // volunteered (e.g. "use the family Gmail account") inline.
        if (defaults.notes) {
          setRows((prev) =>
            prev.some((r) => r.name === 'notes')
              ? prev
              : [
                  ...prev,
                  makeCredentialRow({
                    name: 'notes',
                    value: defaults.notes!,
                    isSecret: false,
                    displayLabel: 'Notes',
                  }),
                ],
          );
        }
      } catch {
        // Silent: the "Use user's email" helper has its own error path,
        // and missing defaults just leave the editor in its initial seed.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, getAccessTokenSilently]);

  const fetchUserEmail = useCallback(async (): Promise<string | null> => {
    if (cachedUserEmail) return cachedUserEmail;
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/human-tasks/${taskId}/user-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const email: string | null = response.data?.userEmail || null;
      if (email) setCachedUserEmail(email);
      if (!email) {
        setError(
          "Couldn't find an email for the task creator. Type the username manually.",
        );
        return null;
      }
      return email;
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Couldn't fetch user email";
      setError(msg);
      return null;
    }
  }, [taskId, cachedUserEmail, getAccessTokenSilently]);

  const generatePassword = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/memories/generate-password',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const generated: string | null = response.data?.password || null;
      if (!generated) {
        setError("Couldn't generate a password. Type one manually.");
        return null;
      }
      return generated;
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Couldn't generate password";
      setError(msg);
      return null;
    }
  }, [getAccessTokenSilently]);

  const hasSecret = hasAtLeastOneSecretValue(rows);
  const hasDuplicates = rowsHaveDuplicateNames(rows).size > 0;
  // Block submit when any row is incomplete (missing name OR missing
  // value). The operator can remove unwanted rows via the × button.
  const hasIncompleteRows = rowIncompletenessByRowId(rows).size > 0;
  const submitOk =
    hasSecret && !hasDuplicates && !hasIncompleteRows && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!submitOk) return;
    setError(null);
    const { fields, secretFieldKeys } = collapseRowsForSubmit(rows);

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        `/human-tasks/${taskId}/credentials/created`,
        {
          fields,
          secretFieldKeys,
          site: site.trim() || undefined,
          label: label.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success && response.data?.credentialId) {
        onSuccess(response.data.credentialId);
      } else {
        setError(response.data?.error || 'Failed to record credential.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to record credential.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [submitOk, rows, site, label, taskId, getAccessTokenSilently, onSuccess]);

  return (
    <div className='credential-creation-form'>
      <div className='credential-creation-form-header'>
        <span className='credential-creation-form-label'>
          Account created during this task
        </span>
        <span className='credential-creation-form-hint'>
          Only fill this in once you have actually created the account on the
          third-party site. At least one field must be marked Secret.
        </span>
      </div>

      <div className='credential-creation-form-row'>
        <label
          className='credential-creation-form-field-label'
          htmlFor='cred-form-site'
        >
          Site
        </label>
        <input
          id='cred-form-site'
          className='credential-creation-form-input'
          type='text'
          value={site}
          onChange={(e) => setSite(e.target.value)}
          placeholder='e.g. freecycle.org'
          disabled={submitting}
        />
      </div>

      <div className='credential-creation-form-row'>
        <label
          className='credential-creation-form-field-label'
          htmlFor='cred-form-label'
        >
          Label (optional)
        </label>
        <input
          id='cred-form-label'
          className='credential-creation-form-input'
          type='text'
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='Short name — defaults to the site'
          disabled={submitting}
          maxLength={120}
        />
      </div>

      <CredentialFieldEditor
        rows={rows}
        onChange={setRows}
        disabled={submitting}
        helpers={{
          generatePassword,
          useUserEmail: fetchUserEmail,
          copyValue: true,
        }}
      />

      {!hasSecret && (
        <div className='credential-creation-form-hint'>
          At least one field must be marked Secret and have a value — a
          credential without a secret is just an account reference. For OAuth /
          magic-link accounts with no password, capture the detail in the task
          notes instead.
        </div>
      )}

      {error && <div className='credential-creation-form-error'>{error}</div>}

      <div className='credential-creation-form-actions'>
        <button
          type='button'
          className='toolbar-btn'
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type='button'
          className='toolbar-btn active'
          onClick={handleSubmit}
          disabled={!submitOk}
        >
          {submitting ? 'Saving…' : 'Save credential'}
        </button>
      </div>
    </div>
  );
};

export default HumanTaskCredentialCreationForm;
